import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  CalendarDays,
  FileText,
  FolderKanban,
  LineChart,
} from "lucide-react";
import { Card } from "../ui/card";
import { Skeleton } from "../ui/skeleton";
import { cn, formatLakhsOrCr } from "../../lib/utils";
import {
  buildConsolidatedCashflowKpiParams,
  type ConsolidatedFilters,
  type ConsolidatedKPIs,
  EMPTY_CONSOLIDATED_KPIS,
  getCurrentFYQuarterLabel,
  normalizeConsolidatedCashflowKpis,
} from "../../lib/consolidated-cashflow";
import apiService from "../../services/apiService";

/** Stable default — inline `{ customerIds: [], lenders: [] }` recreates arrays each render and retriggers fetch. */
const EMPTY_KPI_FILTERS: Pick<ConsolidatedFilters, "customerIds" | "lenders"> = {
  customerIds: [],
  lenders: [],
};

type CashflowStatTone = "teal" | "green" | "amber" | "blue";

const STAT_TONE: Record<CashflowStatTone, { badge: string; icon: string }> = {
  teal: {
    badge: "bg-teal-50 text-teal-700",
    icon: "bg-teal-50 text-teal-600",
  },
  green: {
    badge: "bg-emerald-50 text-emerald-700",
    icon: "bg-emerald-50 text-emerald-600",
  },
  amber: {
    badge: "bg-amber-50 text-amber-700",
    icon: "bg-amber-50 text-amber-600",
  },
  blue: {
    badge: "bg-indigo-50 text-indigo-700",
    icon: "bg-indigo-50 text-indigo-600",
  },
};

function CashflowStatCard({
  title,
  badge,
  value,
  subtitle,
  icon,
  tone,
}: {
  title: string;
  badge: string;
  value: string;
  subtitle: string;
  icon: ReactNode;
  tone: CashflowStatTone;
}) {
  const styles = STAT_TONE[tone];
  return (
    <Card className="border shadow-sm p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {title}
          </p>
          <span
            className={cn(
              "mt-1.5 inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium",
              styles.badge
            )}
          >
            {badge}
          </span>
        </div>
        <div
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
            styles.icon
          )}
        >
          {icon}
        </div>
      </div>
      <p className="mt-4 text-2xl font-semibold tracking-tight text-foreground">
        {value}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
    </Card>
  );
}

type Props = {
  className?: string;
  /** Optional customer/lender filters; omit for all-portfolio KPIs. */
  filters?: Pick<ConsolidatedFilters, "customerIds" | "lenders">;
};

export function ConsolidatedCashflowKpiStrip({
  className,
  filters = EMPTY_KPI_FILTERS,
}: Props) {
  const [kpis, setKpis] = useState<ConsolidatedKPIs>(EMPTY_CONSOLIDATED_KPIS);
  const [loading, setLoading] = useState(true);

  // Primitive keys so parent re-renders / new array refs with same values don't re-fetch
  const customerKey = useMemo(
    () => filters.customerIds.join(","),
    [filters.customerIds]
  );
  const lenderKey = useMemo(
    () => filters.lenders.join(","),
    [filters.lenders]
  );

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      setLoading(true);
      try {
        const params = buildConsolidatedCashflowKpiParams({
          customerIds: customerKey ? customerKey.split(",") : [],
          lenders: lenderKey ? lenderKey.split(",") : [],
        });
        const response = await apiService.get<unknown>(
          "/rental-schedule/get-consolidated-rental-schedule-cashflow-kpis",
          params ? { params } : {}
        );
        if (cancelled) return;
        setKpis(normalizeConsolidatedCashflowKpis(response));
      } catch (error) {
        console.error("Failed to load consolidated cashflow KPIs:", error);
        if (!cancelled) setKpis(EMPTY_CONSOLIDATED_KPIS);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [customerKey, lenderKey]);

  const quarterBadge =
    kpis.quarterLabel?.trim() || getCurrentFYQuarterLabel();

  return (
    <div className={cn("grid grid-cols-2 md:grid-cols-4 gap-3", className)}>
      {loading ? (
        Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-[118px] w-full rounded-lg" />
        ))
      ) : (
        <>
          <CashflowStatCard
            title="Total Invoice Value"
            badge="All time"
            value={formatLakhsOrCr(kpis.totalInvoiceValue)}
            subtitle="Invoices raised to date"
            icon={<FileText className="h-4 w-4" />}
            tone="teal"
          />
          <CashflowStatCard
            title="Cash Flow to Astrafin"
            badge="All time"
            value={formatLakhsOrCr(kpis.cashFlowToAstrafin)}
            subtitle="Net cash retained by Astrafin"
            icon={<FolderKanban className="h-4 w-4" />}
            tone="green"
          />
          <CashflowStatCard
            title="Quarterly Invoice Value"
            badge={quarterBadge}
            value={formatLakhsOrCr(kpis.quarterlyInvoiceValue)}
            subtitle="Invoices raised this quarter"
            icon={<CalendarDays className="h-4 w-4" />}
            tone="amber"
          />
          <CashflowStatCard
            title="Quarterly Cash Flow to Astrafin"
            badge={quarterBadge}
            value={formatLakhsOrCr(kpis.quarterlyCashFlowToAstrafin)}
            subtitle="Net cash flow this quarter"
            icon={<LineChart className="h-4 w-4" />}
            tone="blue"
          />
        </>
      )}
    </div>
  );
}

export default ConsolidatedCashflowKpiStrip;
